import React, { createContext, useContext, useEffect, useState } from "react";
import api, { authStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => authStore.getUser());
  const [loading, setLoading] = useState(false);

  const login = async (email, password, extras = {}) => {
    const payload = { email, password };
    if (extras.captchaToken) payload.captcha_token = extras.captchaToken;
    if (extras.captchaAnswer) payload.captcha_answer = extras.captchaAnswer;
    const { data } = await api.post("/auth/login", payload);
    authStore.setToken(data.token);
    authStore.setUser(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("Logout failed", e);
    }
    authStore.clear();
    setUser(null);
  };

  const refreshUser = (userData) => {
    setUser(userData);
    authStore.setUser(userData);
  };

  useEffect(() => {
    // Silent session hydration: runs exactly once on mount.
    //
    // - If a token is present but the user object is not yet in state (e.g.
    //   returning from a hard refresh), fetch /auth/me to verify the token
    //   is still valid and refresh the cached user profile.
    // - `user` is intentionally excluded from the dep array: we only care
    //   about the mount-time value; subsequent user changes are driven by
    //   login/logout/refreshUser, not by this effect.
    // - `api` is a module import (stable reference), so it does not need to
    //   be listed either.
    const token = authStore.getToken();
    const cachedUser = authStore.getUser();
    if (!token || cachedUser) return;

    let cancelled = false;
    setLoading(true);
    api.get("/auth/me")
      .then((r) => {
        if (cancelled) return;
        setUser(r.data);
        authStore.setUser(r.data);
      })
      .catch(() => { /* keep the app un-authenticated; ProtectedRoute redirects */ })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
